import { memo, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isNil, isObject } from 'lodash';
// Import Types
import type { CommunicationListRef } from 'components/CommunicationList/CommunicationList';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
import { useListFilters } from 'hooks/useListFilters';
// Import Components
import DataTableHeaderFilter from 'components/common/DataTableHeaderFilter';
import { CommunicationList } from 'components/CommunicationList';
// Import Services
import { PromotionHttpService, TemplateHttpService, AwardHttpService } from 'services/http';
// Import Helpers
import { getFilterHolderKey } from 'services/Helpers';
// Import Enums
import { DataTableConfigKeysEnum } from 'enums';
// Import Constants
import { DEFAULT_SORTING, FilterComparisons, CommunicationPromotionTypeLabels } from 'constants/common';
import { CommunicationListFilterConfig, getCommunicationsRequestBody } from './config';
import { campaignPageCommunicationInitialState } from 'constants/listDefaultFilters';

const { defaultInputsData } = CommunicationListFilterConfig;
const { defaultInputsDataList, defaultInputsDataIds } = defaultInputsData;

const eventKey = 'campaignPageCommunicationList';

const getRequestBody = (name, value) => {
    return {
        Filters: [{ Name: name, Comparision: FilterComparisons.In, Values: value }],
        Pageing: { PageSize: 100, PageNumber: 1 },
        Sorting: DEFAULT_SORTING,
    };
};

interface Props {
    campaignId: string;
}

const CampaignCommunicationList: React.FC<Props> = ({ campaignId }) => {
    const { search } = useLocation();
    const { doPostRequest } = useRequest();

    const filterHolderQueryParams = useQueryParams(
        getFilterHolderKey(DataTableConfigKeysEnum.dataTableCampaignCommunication),
    );

    const listFilters = useListFilters(campaignPageCommunicationInitialState);
    const { isMounted, generateAndChangeFiltersForBaseRequestBody } = listFilters;

    const defaultFilters = useMemo(
        () =>
            getCommunicationsRequestBody('PromotionId', [
                `${CommunicationPromotionTypeLabels.Campaign} , ${campaignId}`,
            ]),
        [campaignId],
    );

    const campaignCommunicationListRef = useRef<CommunicationListRef>(null);

    const { getTemplatesRequest, getAwardsListRequest, getPromotionsRequest } = useCancelableRequests({
        getTemplatesRequest: TemplateHttpService.getTemplateList,
        getAwardsListRequest: AwardHttpService.getAwardsList,
        getPromotionsRequest: PromotionHttpService.getPromotionList,
    });

    const onFilterApply = (filtersFields, isInit = false) => {
        const { tmpFilters } = generateAndChangeFiltersForBaseRequestBody({
            filtersFields,
            defaultInputsDataList,
            defaultFilters: defaultFilters.Filters,
            shouldResetPage: !isInit,
        });

        if (campaignCommunicationListRef.current) {
            campaignCommunicationListRef.current.Filters = tmpFilters;
        }
        if (!isInit) {
            campaignCommunicationListRef.current?.reset();
        }
    };

    const onResetFilters = () => {
        generateAndChangeFiltersForBaseRequestBody({
            defaultInputsDataList,
            defaultFilters: defaultFilters.Filters,
        });
        campaignCommunicationListRef.current?.reset();
    };

    // Init part
    const getTemplates = (value) => {
        return new Promise((resolve, reject) => {
            if (!isNil(value)) {
                doPostRequest(getTemplatesRequest.request, {
                    requestBody: getRequestBody('TemplateId', value),
                    successCallback: (data) => {
                        defaultInputsDataList[defaultInputsDataIds.TemplateId].data = isObject(data)
                            ? data.Data.map((item) => ({ label: item.Name, value: item.TemplateId }))
                            : [];
                        resolve({ isTemplatesReady: true });
                    },
                }).then(() => reject({ isTemplatesReady: false }));
            } else {
                resolve({ isTemplatesReady: true });
            }
        });
    };

    const getAwards = (value) => {
        return new Promise((resolve, reject) => {
            if (!isNil(value)) {
                doPostRequest(getAwardsListRequest.request, {
                    requestBody: getRequestBody('BonusId', value),
                    successCallback: (data) => {
                        defaultInputsDataList[defaultInputsDataIds.BonusId].data = isObject(data)
                            ? data.Data.map((item) => ({ label: item.Name, value: item.Id }))
                            : [];
                        resolve({ isAwardsReady: true });
                    },
                    errorCallback: () => reject({ isAwardsReady: false }),
                }).then(() => reject({ isAwardsReady: false }));
            } else {
                resolve({ isAwardsReady: true });
            }
        });
    };

    const getPromotions = (value) => {
        return new Promise((resolve, reject) => {
            if (!isNil(value)) {
                doPostRequest(getPromotionsRequest.request, {
                    requestBody: getRequestBody('Id', [value]),
                    successCallback: (data) => {
                        // TODO: check if this needed or not
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        defaultInputsDataList[defaultInputsDataIds.PromotionId].data = isObject(data)
                            ? data.Data.map((item) => ({
                                  value: item.Id,
                                  label: item.Name,
                                  type: item.Type,
                                  status: item.Status,
                              }))
                            : [];
                        resolve({ isAwardsReady: true });
                    },
                    errorCallback: () => reject({ isPromotionsReady: false }),
                }).then(() => {
                    reject({ isPromotionsReady: false });
                });
            } else {
                resolve({ isPromotionsReady: true });
            }
        });
    };

    const getHeaderActions = () => {
        return (
            <DataTableHeaderFilter
                defaultInputsData={defaultInputsDataList}
                onApply={onFilterApply}
                onReset={onResetFilters}
            />
        );
    };

    const init = () => {
        let params: Nullable<AnyObject> = null;
        if (search) {
            params = filterHolderQueryParams.decode();
        }
        isMounted.current = true;
        onFilterApply(isObject(params) ? params : {}, true);

        params = params as Nullable<AnyObject>;
        Promise.all([getTemplates(params?.TemplateId), getAwards(params?.BonusId), getPromotions(params?.PromotionId)]);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className="crm-object-list-page-wrapper crm-report-list-page-wrapper report-list-page-c communication-list-wrapper crm-report-communications-wrapper">
            <div className="crm-section crm-section-full-height">
                <CommunicationList
                    ref={campaignCommunicationListRef}
                    tableKey={DataTableConfigKeysEnum.dataTableCampaignCommunication}
                    campaignId={campaignId}
                    headerLeftActions={getHeaderActions()}
                    eventKey={eventKey}
                    listFilters={listFilters}
                    isHasLeftHeaderActions={true}
                    isShowVerticalDots={false}
                    isShowColumnChooser={true}
                    shouldShowExecutionId={true}
                    shouldSendFileNameForDownload={true}
                />
            </div>
        </div>
    );
};

export default memo(CampaignCommunicationList);
